/** AutoCAD's in-place text editor — the thing you get by double-clicking a note.
 *
 * ⚠⚠ **THERE IS NO BUTTON FOR THIS IN AutoCAD AND THERE IS NOT ONE HERE EITHER.** MTEXT is
 * edited by double-clicking it, full stop, and a drafter who double-clicks and gets nothing
 * concludes the text is not editable at all. `MTEXT` is the third most common entity in this
 * office's corpus — 64.6% of 11,734 drawings — and until now the only way to change a note was
 * to erase it and draw another.
 *
 * ⚠⚠⚠ **IT EDITS THE RAW STRING, INCLUDING THE INLINE CODES, AND THAT IS NOT LAZINESS.**
 * `display.py` sends `plain_text()`, which strips every code. An editor that read the drawn
 * string and wrote it back would delete a client's formatting the first time anybody fixed a
 * typo — and measured over the 7,441 MTEXT entities stored here, **77% carry at least one
 * inline code**. Most of that is a single `\A` alignment prefix (57%) rather than rich text —
 * colour, font and height runs are 3–6% each — but "mostly one code" is not a reason to destroy
 * the code.
 *
 * ⇒ So `\P` is shown as a real line break, because that is what it means, and every other code
 * is shown as it is and written back byte for byte. When there ARE other codes the editor says
 * so, rather than letting a drafter discover `{\C1;` in the middle of their sentence and delete
 * it as a typo.
 */
import { useEffect, useRef, useState } from "react";

export interface TextSpec {
  handle: string;
  kind: string;
  text: string;
  height: number;
  width: number;
  rotation: number;
  attachment: number;
  line_spacing: number;
  bg_fill: number;
  bg_colour: number;
  bg_scale: number;
}

export interface TextBox { x: number; y: number; w: number; h: number }

/** ⚠ Every code EXCEPT `\P`, which this editor turns into a real line break. A leading `\A0;`
 *  through `\A2;` is a vertical alignment for the whole string and is the commonest of all —
 *  it is matched here so the warning does not fire on 57% of the office's notes for something
 *  that is not rich text at all. */
const RICH = /\\[LlOoSfFCcHWwQTpAK]|\{\\|\\~/;
const ONLY_ALIGN = /^\\A[0-2];/;

export function hasRichCodes(s: string): boolean {
  return RICH.test(s.replace(ONLY_ALIGN, ""));
}

/** `\P` is a paragraph break; a real newline is what a person types. ⚠ `\\P` — an ESCAPED
 *  backslash followed by P — is a literal backslash and a P, not a break, so the replacement
 *  has to skip a preceding escape rather than matching `\P` anywhere it appears. */
const PARK = "\u0000";
export function toEditable(raw: string): string {
  // ⚠ A SPACE would be a disastrous placeholder here — the text is full of them. `\u0000`
  // cannot appear in a DXF string, so parking escaped backslashes on it is safe.
  return raw.split("\\\\").join(PARK).split("\\P").join("\n").split(PARK).join("\\\\");
}
export function fromEditable(s: string): string {
  return s.split("\\\\").join(PARK).split("\n").join("\\P").split(PARK).join("\\\\");
}

/** AutoCAD's nine attachment points, in its own order. */
const ATTACH = ["Top left", "Top center", "Top right", "Middle left", "Middle center",
                "Middle right", "Bottom left", "Bottom center", "Bottom right"];

export default function TextEditor(
  { spec, box, dark, onCommit, onCancel }:
  { spec: TextSpec; box: TextBox; dark: boolean;
    onCommit: (s: string, props: Partial<TextSpec>) => void; onCancel: () => void },
) {
  const [text, setText] = useState(() => toEditable(spec.text));
  const [height, setHeight] = useState(spec.height);
  const [width, setWidth] = useState(spec.width);
  const [attach, setAttach] = useState(spec.attachment);
  const [bg, setBg] = useState(spec.bg_fill);
  const ta = useRef<HTMLTextAreaElement>(null);
  const rich = hasRichCodes(spec.text);
  const mtext = spec.kind === "MTEXT";

  // ⚠ Focused and fully selected on open — AutoCAD's editor opens with the text selected, so
  // typing replaces it. Landing with an empty caret makes a drafter click again to select.
  useEffect(() => { ta.current?.focus(); ta.current?.select(); }, []);

  const commit = () => {
    const props: Partial<TextSpec> = {};
    if (height !== spec.height) props.height = height;
    if (mtext && width !== spec.width) props.width = width;
    if (mtext && attach !== spec.attachment) props.attachment = attach;
    if (mtext && bg !== spec.bg_fill) props.bg_fill = bg;
    onCommit(fromEditable(text), props);
  };

  const ui = dark
    ? { bg: "#1a1d21", fg: "#e8eaed", line: "#3c4043", soft: "#9aa0a6", warn: "#f0a30a" }
    : { bg: "#ffffff", fg: "#202124", line: "#c8ccd0", soft: "#5f6368", warn: "#b06000" };
  const field: React.CSSProperties = {
    background: dark ? "#26292d" : "#f1f3f4", color: ui.fg,
    border: `1px solid ${ui.line}`, borderRadius: 2, font: "11px system-ui",
    padding: "1px 3px", width: 62,
  };

  return (
    <div data-testid="mtext-editor"
      style={{ position: "absolute", left: Math.max(4, box.x), top: Math.max(4, box.y),
               zIndex: 40, background: ui.bg, color: ui.fg,
               border: `1px solid ${dark ? "#5aa9ff" : "#0b57d0"}`, borderRadius: 3,
               boxShadow: "0 6px 24px rgba(0,0,0,.45)", font: "12px system-ui" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 6px",
                    borderBottom: `1px solid ${ui.line}`, fontSize: 11, color: ui.soft }}>
        <b style={{ color: ui.fg }}>{spec.kind}</b>
        <label>H<input type="number" value={height} step="any" style={{ ...field, marginLeft: 3 }}
                       onChange={(e) => setHeight(Number(e.target.value))} /></label>
        {mtext && (
          // ⚠ 0 is AutoCAD's "no wrap" and is a legal, common value — not an empty field.
          <label title="Box width — 0 does not wrap">
            W<input type="number" value={width} step="any" min={0}
                    style={{ ...field, marginLeft: 3 }}
                    onChange={(e) => setWidth(Number(e.target.value))} /></label>
        )}
        {mtext && (
          <select value={attach} onChange={(e) => setAttach(Number(e.target.value))}
                  style={{ ...field, width: 96 }} title="Justification">
            {ATTACH.map((n, i) => <option key={n} value={i + 1}>{n}</option>)}
          </select>
        )}
        {mtext && (
          // ⚠ The background mask is what makes a note readable over a hatch, and this office's
          // drawings are 61.7% hatched. One checkbox, one real DXF attribute.
          <label title="Background mask"><input type="checkbox" checked={bg !== 0}
                 onChange={(e) => setBg(e.target.checked ? 1 : 0)} /> Mask</label>
        )}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 10 }}>Esc cancels · Ctrl+Enter saves</span>
      </div>
      {rich && (
        <div style={{ padding: "3px 6px", fontSize: 10.5, color: ui.warn,
                      borderBottom: `1px solid ${ui.line}`, maxWidth: 420 }}>
          ⚠ This note carries inline formatting codes. They are shown as they are stored and
          saved unchanged — editing around them keeps the formatting, deleting one removes it.
        </div>
      )}
      <textarea ref={ta} value={text} spellCheck={false}
        data-testid="mtext-editor-input"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          // ⚠⚠ Enter inserts a LINE BREAK — this is multi-line text and Enter is how a person
          // makes a new line. Ctrl+Enter commits, which is what AutoCAD's editor does too.
          if (e.key === "Escape") { e.preventDefault(); onCancel(); }
          if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); commit(); }
          // ⚠ Every key stops here. The canvas listens for L, C, E and every other command
          // letter on the window, so without this typing a note would fire commands.
          e.stopPropagation();
        }}
        onBlur={commit}
        style={{ display: "block", width: Math.min(560, Math.max(220, box.w)),
                 height: Math.min(360, Math.max(56, box.h)),
                 background: dark ? "#0f1113" : "#fff", color: ui.fg, border: "none",
                 outline: "none", resize: "both", padding: "6px 8px",
                 font: `${Math.max(11, Math.min(22, 13))}px ui-monospace, monospace`,
                 lineHeight: 1.35, whiteSpace: "pre" }} />
    </div>
  );
}
